function escapeHtml(text) {
  const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
  return String(text || '').replace(/[&<>"']/g, (m) => map[m]);
}

function renderList(title, values = []) {
  if (!Array.isArray(values) || values.length === 0) return '';
  const items = values.map((v) => `<li>${escapeHtml(v)}</li>`).join('');
  return `
    <section class="block">
      <h3>${escapeHtml(title)}</h3>
      <ul>${items}</ul>
    </section>
  `;
}

const BRAND_LOGO_DATA_URI = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 256 256' role='img' aria-label='PageHyve icon'%3E%3Cpolygon fill='%234FAFB3' points='96.0,52.0 256.0,52.0 220.0,92.0 60.0,92.0' /%3E%3Cpolygon fill='%230F172A' points='84.0,106.0 244.0,106.0 208.0,146.0 48.0,146.0' /%3E%3Cpolygon fill='%230F172A' points='72.0,160.0 232.0,160.0 196.0,200.0 36.0,200.0' /%3E%3C/svg%3E";

export function buildProposalHTML(proposal = {}, settings = {}) {
  const {
    proposalNumber = 'PREVIEW',
    title = '',
    preparedBy = '',
    clientName = '',
    clientEmail = '',
    clientCompany = '',
    executiveSummary = '',
    scopeIncluded = [],
    scopeExcluded = [],
    deliverables = [],
    assumptions = [],
    timeline = [],
    terms = {},
    items = [],
    subtotal = 0,
    discount = 0,
    tax = 0,
    total = 0,
    expiresAt,
    notes = '',
    status = 'draft'
  } = proposal;

  const companyName = settings?.companyName || 'PageHyve Studios';
  const companyAddress = settings?.billingAddress || settings?.address || '';
  const secondaryContact = [settings?.billingEmail || settings?.email || '', settings?.billingPhone || settings?.phone || ''].filter(Boolean).join(' | ');
  const contactLine = secondaryContact ? `${companyAddress ? `${companyAddress}<br>` : ''}${secondaryContact}` : companyAddress;
  const heading = title || `Proposal ${proposalNumber}`;
  const statusLabel = String(status || 'draft').toUpperCase();

  const formattedItems = (items || []).map((item) => {
    const qty = Number(item.quantity || 0);
    const rate = Number(item.rate || 0);
    return `
      <tr>
        <td>${escapeHtml(item.name)}</td>
        <td style="text-align:right">${qty}</td>
        <td style="text-align:right">$${rate.toFixed(2)}</td>
        <td style="text-align:right">$${(qty * rate).toFixed(2)}</td>
      </tr>
    `;
  }).join('');

  const timelineRows = (timeline || []).filter((row) => row?.phase || row?.duration || row?.details).map((row) => `
    <tr>
      <td>${escapeHtml(row.phase || '')}</td>
      <td>${escapeHtml(row.duration || '')}</td>
      <td>${escapeHtml(row.details || '')}</td>
    </tr>
  `).join('');

  return `<!doctype html>
  <html>
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width,initial-scale=1" />
    <title>${escapeHtml(heading)}</title>
    <style>
      :root{--ink:#0f172a;--soft:#5b6b81;--line:#d8e3f2;--accent:#0ea5e9;--accent-deep:#0369a1;--bg:#f5f8ff}
      body{font-family:"Avenir Next","Segoe UI","Helvetica Neue",Arial,sans-serif;margin:0;padding:1.2rem;background:radial-gradient(circle at top right,#dbeafe 0%,var(--bg) 45%,#eef6ff 100%);color:var(--ink)}
      .sheet{max-width:920px;margin:0 auto;background:#fff;border:1px solid var(--line);border-radius:14px;padding:1.6rem;box-shadow:0 24px 50px rgba(15,23,42,.12)}
      header{display:flex;justify-content:space-between;align-items:center;gap:1rem;margin-bottom:1.5rem;border-bottom:1px solid var(--line);padding-bottom:1rem}
      h1{margin:0;font-size:1.4rem}
      h2{margin:0.8rem 0 0.35rem}
      h3{margin:0 0 0.45rem}
      .meta{font-size:.9rem;color:var(--soft)}
      .badge{display:inline-block;background:linear-gradient(135deg,var(--accent) 0%,var(--accent-deep) 100%);color:#f0f9ff;padding:.32rem .85rem;border-radius:999px;font-weight:700;font-size:.78rem;letter-spacing:.06em}
      .status{margin-top:.45rem;font-size:.78rem;color:#0b3e5d;font-weight:700}
      .lead{margin-top:0.5rem;padding:0.8rem 0.9rem;border:1px solid var(--line);border-radius:10px;background:#f8fbff;color:#24364c;white-space:pre-wrap}
      .grid{display:grid;grid-template-columns:1fr 1fr;gap:0.8rem}
      .block{margin-top:1rem;padding:0.9rem;border:1px solid var(--line);border-radius:10px;background:#fff}
      ul{margin:0.2rem 0 0;padding-left:1.2rem}
      li{margin:0.2rem 0}
      table{width:100%;border-collapse:collapse;margin-top:0.8rem}
      th{background:#f8fbff;border:1px solid var(--line);padding:10px;text-align:left}
      td{border:1px solid var(--line);padding:10px}
      .terms p{margin:0.35rem 0}
      .summary-box{margin-top:1.2rem;border:1px solid var(--line);border-radius:10px;padding:.9rem 1rem;background:#f8fbff}
      @media (max-width:700px){.grid{grid-template-columns:1fr}}
      @media print{body{background:#fff;padding:0}.sheet{box-shadow:none;border:0}}
    </style>
  </head>
  <body>
    <div class="sheet">
      <header>
        <div style="display:flex;align-items:center;gap:0.75rem">
          <img src="${BRAND_LOGO_DATA_URI}" alt="PageHyve" style="width:60px;height:60px" />
          <div>
            <h1>${escapeHtml(companyName)}</h1>
            <div class="meta">${contactLine || ''}</div>
          </div>
        </div>
        <div style="text-align:right">
          <span class="badge">PROPOSAL</span>
          <div style="font-weight:700;font-size:1.125rem;margin-top:.5rem">${escapeHtml(proposalNumber)}</div>
          <div class="meta">Expires: ${expiresAt ? new Date(expiresAt).toLocaleDateString() : 'N/A'}</div>
          <div class="status">STATUS: ${escapeHtml(statusLabel)}</div>
        </div>
      </header>

      <section>
        <h2>${escapeHtml(heading)}</h2>
        <div class="grid">
          <div class="block">
            <div class="meta">Client</div>
            <div style="font-weight:600;margin-top:0.25rem">${escapeHtml(clientName)}</div>
            <div class="meta">${escapeHtml(clientCompany)}</div>
            <div class="meta">${escapeHtml(clientEmail)}</div>
          </div>
          <div class="block">
            <div class="meta">Prepared By</div>
            <div style="font-weight:600;margin-top:0.25rem">${escapeHtml(preparedBy || companyName)}</div>
            <div class="meta">Date: ${new Date().toLocaleDateString()}</div>
          </div>
        </div>
      </section>

      ${executiveSummary ? `<section class="block"><h3>Executive Summary</h3><div class="lead">${escapeHtml(executiveSummary)}</div></section>` : ''}
      ${renderList('Scope Included', scopeIncluded)}
      ${renderList('Deliverables', deliverables)}
      ${renderList('Scope Excluded', scopeExcluded)}

      ${timelineRows ? `<section class="block"><h3>Approach & Timeline</h3><table><thead><tr><th>Phase</th><th>Duration</th><th>Details</th></tr></thead><tbody>${timelineRows}</tbody></table></section>` : ''}

      <section class="block">
        <h3>Investment Estimate</h3>
        <table>
          <thead>
            <tr>
              <th>Description</th>
              <th style="text-align:right;">Qty</th>
              <th style="text-align:right;">Rate</th>
              <th style="text-align:right;">Amount</th>
            </tr>
          </thead>
          <tbody>${formattedItems}</tbody>
          <tfoot>
            <tr><td colspan="3" style="text-align:right;">Subtotal</td><td style="text-align:right;">$${Number(subtotal || 0).toFixed(2)}</td></tr>
            <tr><td colspan="3" style="text-align:right;">Discount</td><td style="text-align:right;">$${Number(discount || 0).toFixed(2)}</td></tr>
            <tr><td colspan="3" style="text-align:right;">Tax</td><td style="text-align:right;">$${Number(tax || 0).toFixed(2)}</td></tr>
            <tr><td colspan="3" style="text-align:right;font-weight:800;">Total</td><td style="text-align:right;font-weight:800;">$${Number(total || 0).toFixed(2)}</td></tr>
          </tfoot>
        </table>
      </section>

      ${renderList('Assumptions & Dependencies', assumptions)}

      ${(terms?.paymentSchedule || terms?.revisionPolicy || terms?.validity || terms?.nextSteps) ? `
        <section class="block terms">
          <h3>Terms</h3>
          ${terms?.paymentSchedule ? `<p><strong>Payment Schedule:</strong> ${escapeHtml(terms.paymentSchedule)}</p>` : ''}
          ${terms?.revisionPolicy ? `<p><strong>Revision Policy:</strong> ${escapeHtml(terms.revisionPolicy)}</p>` : ''}
          ${terms?.validity ? `<p><strong>Validity:</strong> ${escapeHtml(terms.validity)}</p>` : ''}
          ${terms?.nextSteps ? `<p><strong>Next Steps:</strong> ${escapeHtml(terms.nextSteps)}</p>` : ''}
        </section>
      ` : ''}

      ${notes ? `<section class="block"><h3>Additional Notes</h3><div class="lead">${escapeHtml(notes)}</div></section>` : ''}
      <div class="summary-box">This proposal is valid until ${expiresAt ? new Date(expiresAt).toLocaleDateString() : 'the date specified above'}.</div>
    </div>
  </body>
  </html>`;
}
